import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
// helpers
import * as Yup from 'yup';
import _ from 'lodash';

// mui
import { Grid, Box, Card, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { ToggleButton, ToggleButtonGroup } from '@mui/lab';
// components
import RenewalFlowQuestions from '../../_global/renewals/RenewalFlowQuestions';
import { InputLabelBase } from '../../../components/inputs/InputLabelBase';
import { EditSection } from '../../../public/quote/confirm/EditSection';
import { CustomerPrime250WithRenewalChanges } from '../../workflows/accounts-renewals/prime250/policyholder/CustomerPrime250WithRenewalChanges';
import CustomerRenewalAttestSubmit from '../../workflows/accounts-renewals/prime100/policyholder/CustomerRenewalAttestSubmit';
// utils
import { Modal } from '../../../components/modals/v2/helpers/v2.modal.helpers';
import { InvalidateQuotesConfig } from '../../../components/modals/InvalidateQuotes';

const schema = Yup.object().shape({
  backupFrequency: Yup.string().required().label('Backup Frequency'),
  backupType: Yup.array().when('backupFrequency', {
    is: 'NEVER',
    then: Yup.array().label('Back-up Type'),
    otherwise: Yup.array()
      .min(1, 'Please select at least one back up option')
      .required()
      .label('Back-up Type'),
  }),
  backupFrequencyOther: Yup.string().when('backupType', {
    is: (backupType) =>
      typeof backupType !== 'undefined' &&
      backupType.includes('backupFrequencyOther') === true,
    then: Yup.string()
      .required()
      .typeError('Back-up is a required field')
      .label('Back-up Type'),
    otherwise: Yup.string().label('Back-up Type'),
  }),
  mfaAuthentication: Yup.bool()
    .required()
    .label('MFA Authentication')
    .typeError('MFA Authentication is required'),
  mfaType: Yup.array().when('mfaAuthentication', {
    is: true || 'true',
    then: Yup.array()
      .min(1, 'Please select at least one MFA option')
      .required()
      .label('MFA Type'),
    otherwise: Yup.array().label('MFA Type'),
  }),
  mfaOther: Yup.string().when('mfaType', {
    is: (mfaType) =>
      typeof mfaType !== 'undefined' && mfaType.includes('mfaOther') === true,
    then: Yup.string().when('mfaAuthentication', {
      is: true || 'true',
      then: Yup.string()
        .required()
        .typeError('MFA Other is a required field')
        .label('MFA Other'),
      otherwise: Yup.string().typeError('MFA Other is a required field!'),
    }),
    otherwise: Yup.string().typeError('MFA Other is a required field!'),
  }),
  incidentResponsePlan: Yup.bool()
    .required()
    .typeError('Incident Response Plan is a required field')
    .label('Incident Response Plan'),
  changeInScope: Yup.bool().required().label('Change in scope'),
  changeInScopeDetail: Yup.string().when('changeInScope', {
    is: true || 'true',
    then: Yup.string()
      .required()
      .typeError('Change in Scope is a required field')
      .label('Change in Scope Details'),
    otherwise: Yup.string().label('Change in Scope Plan'),
  }),
});

const LabelProps = {
  required: true,
  TypographyProps: {
    variant: 'body2',
    gutterBottom: true,
  },
};

const CustomerP250RenewalAttestation = ({ data = {}, onMovingAhead }: any) => {
  const { formattedQuote: quote } = data;
  const classes = useStyles();

  const defaultValues = {};

  const [firmoOrSecurityAnswersChanged, setFirmoOrSecurityAnswersChanged] =
    useState(true);

  useEffect(() => {
    setDefaultValues(defaultValues, quote);
    const { renewalChangeInAnswers } = quote;
    const uiRenewalChangeInAnswers = getUiRenewalChangeInAnswers(
      renewalChangeInAnswers
    );
    setFirmoOrSecurityAnswersChanged(uiRenewalChangeInAnswers);

    // eslint-disable-next-line
  }, []);

  const { handleSubmit, ...methods } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const handleChange = (event: any, newAnswer: any) => {
    setFirmoOrSecurityAnswersChanged(newAnswer);
  };

  const values = methods.getValues();

  const toggleButtonProps = {
    disabled: false,
    className: classes.toggleButton,
    classes: {
      disabled: classes.toggleButtonDisabled,
    },
  };

  const onSubmit = () => {
    const backupFrequency = _.get(values, 'backupFrequency');
    const mfaType = _.get(values, 'mfaType', []);
    const backupType = _.get(values, 'backupType', []);
    const securityQuestionsPayload = {
      renewalChangeInAnswers: firmoOrSecurityAnswersChanged,
      accountId: quote.accountId,
      mfaAuthentication: _.get(values, 'mfaAuthentication'),
      mfaEmail:
        _.get(values, 'mfaAuthentication') === true
          ?
            mfaType.includes('mfaEmail')
          : false,
      mfaCloudDeployments:
        _.get(values, 'mfaAuthentication') === true
          ?
            mfaType.includes('mfaCloudDeployments')
          : false,
      mfaMissionCriticalSystems: _.get(values, 'mfaAuthentication')
        ?
          mfaType.includes('mfaMissionCriticalSystems')
        : false,
      mfaRemoteAccess:
        _.get(values, 'mfaAuthentication') === true
          ?
            mfaType.includes('mfaRemoteAccess')
          : false,
      mfaOther:
        _.get(values, 'mfaAuthentication') !== true
          ? ''
          : _.get(values, 'mfaOther'),
      changeInScope: _.get(values, 'changeInScope'),
      changeInScopeDetail: _.get(values, 'changeInScopeDetail'),
      incidentResponsePlan: _.get(values, 'incidentResponsePlan'),
      p250Details: {
        backupFrequency,
        backupFrequencyEncrypted:
          backupFrequency !== 'NEVER'
            ?
              backupType.includes('backupFrequencyEncrypted')
            : false,
        backupFrequencyTested:
          backupFrequency !== 'NEVER'
            ?
              backupType.includes('backupFrequencyTested')
            : false,
        backupFrequencySeparate:
          backupFrequency !== 'NEVER'
            ?
              backupType.includes('backupFrequencySeparate')
            : false,
        backupFrequencyOther:
          backupFrequency !== 'NEVER'
            ? _.get(values, 'backupFrequencyOther')
            : false,
      },
    };

    Modal.show(InvalidateQuotesConfig, {
      data: {
        update: 'questions',
        reset: methods.reset,
        ...securityQuestionsPayload,
      },
      config: {
        title: `Account Changes for ${quote.name}`,
      },
    });
  };

  return (
    <Card>
      <Grid>
        <Box className={classes.securityQuestion}>
          <InputLabelBase {...LabelProps}>
            Has there been any change to the security answers you provided to
            Cowbell in the prior-year’s application for insurance?
          </InputLabelBase>
          <ToggleButtonGroup
            value={firmoOrSecurityAnswersChanged}
            exclusive
            onChange={handleChange}
          >
            <ToggleButton value {...toggleButtonProps}>
              <div>Yes</div>
            </ToggleButton>
            <ToggleButton value={false} {...toggleButtonProps}>
              <div>No</div>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* dont make changes to the below file for things not related to renewal */}
        {firmoOrSecurityAnswersChanged ? (
          <CustomerPrime250WithRenewalChanges
            data={data}
            onMovingAhead={onMovingAhead}
          />
        ) : (
          <Box className={classes.additionalSecurityQuestions}>
            <Typography variant="h1" className={classes.heading}>
              Additional Security Questions
            </Typography>
            <FormProvider {...methods} handleSubmit={handleSubmit}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <RenewalFlowQuestions isRenewal methods={methods} />

                <EditSection show />
              </form>
            </FormProvider>

            {/* dont make changes to the below file for things not related to renewal */}
            <CustomerRenewalAttestSubmit
              data={quote}
              onMovingAhead={onMovingAhead}
            />
          </Box>
        )}
      </Grid>
    </Card>
  );
};

const getUiRenewalChangeInAnswers = (renewalChangeInAnswers: any) => {
  if (_.isUndefined(renewalChangeInAnswers)) {
    return true;
  }
  if (_.isNull(renewalChangeInAnswers)) {
    return true;
  }
  return renewalChangeInAnswers;
};

const setDefaultValues = (defaultValues: any, quote: any) => {
  function trueCheckDetails(key: any) {
    if (quote[key]) {
      return true;
    }
    return false;
  }

  let mfaType = [
    'mfaEmail',
    'mfaCloudDeployments',
    'mfaMissionCriticalSystems',
    'mfaRemoteAccess',
    'mfaOther',
  ];
  let backupType = [
    'backupFrequencyEncrypted',
    'backupFrequencyTested',
    'backupFrequencySeparate',
    'backupFrequencyOther',
  ];

  mfaType = mfaType.filter(trueCheckDetails);
  backupType = backupType.filter(trueCheckDetails);

  defaultValues.mfaType = mfaType;
  defaultValues.backupType = backupType;
  defaultValues.backupFrequency = quote.backupFrequency;
  defaultValues.backupFrequencyEncrypted = quote.backupFrequencyEncrypted;
  defaultValues.backupFrequencyOther = quote.backupFrequencyOther;
  defaultValues.backupFrequencySeparate = quote.backupFrequencySeparate;
  defaultValues.backupFrequencyTested = quote.backupFrequencyTested;
  defaultValues.mfaAuthentication = quote.mfaAuthentication;
  defaultValues.mfaCloudDeployments = quote.mfaCloudDeployments;
  defaultValues.mfaEmail = quote.mfaEmail;
  defaultValues.mfaMissionCriticalSystems = quote.mfaMissionCriticalSystems;
  defaultValues.mfaOther = quote.mfaOther;
  defaultValues.mfaRemoteAccess = quote.mfaRemoteAccess;
  defaultValues.changeInScope = quote.changeInScope;
  defaultValues.changeInScopeDetail = quote.changeInScopeDetail;
  defaultValues.incidentResponsePlan = quote.incidentResponsePlan;
};

const useStyles = makeStyles(({ config, palette }) => ({
  securityQuestion: {
    padding: '1.6666666667rem',
  },
  additionalSecurityQuestions: {
    padding: '1.6666666667rem',
  },
  heading: {
    marginBottom: '2rem',
    fontSize: config.textSizes.canon,
    lineHeight: 1,
    textAlign: 'left',
    color: `${palette.primary.contrastText} !important`,
    fontWeight: config.weights.bold,
  },
  toggleButton: {
    padding: '0.5rem 4rem',
  },
  toggleButtonDisabled: {
    color: `${palette.primary.contrastText} !important`,
  },
}));

export default CustomerP250RenewalAttestation;
